import { z } from 'zod';
import type { RatingGrade } from './types.js';

/** Lebenszyklus einer Fläche (Plan 03 Abschnitt 6.1) */
export const AREA_STATUSES = ['pool', 'lead', 'in_akquise', 'begangen', 'geplant', 'bepflanzt', 'im_monitoring', 'gesichert', 'abgelehnt', 'inaktiv'] as const;
export type AreaStatus = (typeof AREA_STATUSES)[number];
export const AREA_STATUS_LABELS: Record<AreaStatus, string> = {
  pool: 'Pool',
  lead: 'Lead',
  in_akquise: 'In Akquise',
  begangen: 'Begangen',
  geplant: 'Geplant',
  bepflanzt: 'Bepflanzt',
  im_monitoring: 'Im Monitoring',
  gesichert: 'Gesichert',
  abgelehnt: 'Abgelehnt',
  inaktiv: 'Inaktiv',
};
/** Pill-Farbe je Status (Klassen der StatusPill) */
export const AREA_STATUS_PILL: Record<AreaStatus, string> = {
  pool: 'muted',
  lead: 'abstimmung',
  in_akquise: 'abstimmung',
  begangen: 'zugesagt',
  geplant: 'zugesagt',
  bepflanzt: 'bestaetigt',
  im_monitoring: 'abstimmung',
  gesichert: 'bestaetigt',
  abgelehnt: 'offen',
  inaktiv: 'muted',
};

const dec = z.number().nullable().optional();
const str = (n: number) => z.string().max(n).nullable().optional();

export const AreaBaseSchema = z.object({
  name: z.string().min(1, 'Name ist Pflicht').max(200),
  state_code: str(5),
  region: str(100),
  city: str(120),
  zip: str(10),
  municipality: str(120),
  district: str(120),
  flur: str(60),
  parcel: str(60),
  compartment: str(60),
  lat: dec,
  lng: dec,
  hectares: dec,
  forester_contact_id: z.number().int().nullable().optional(),
  owner_contact_id: z.number().int().nullable().optional(),
  owner_name_text: str(200),
  ownership_type: str(50),
  certification: str(50),
  water_protection: str(120),
  soil: z.string().nullable().optional(),
  wildlife_protection: str(50),
  ecological_need: z.string().nullable().optional(),
  neighbour_potential: z.string().nullable().optional(),
  rescue_point: str(60),
  access_note: z.string().nullable().optional(),
  parking_lat: dec,
  parking_lng: dec,
  planting_season_code: z.enum(['F', 'S', 'H']).nullable().optional(),
  planting_year: z.number().int().min(1990).max(2100).nullable().optional(),
  planted_at: str(10),
  trees_planted: z.number().int().nullable().optional(),
  notes: z.string().nullable().optional(),
  polygons: z.array(z.object({ seq: z.number().int(), lat: z.number(), lng: z.number(), hectares: z.number().nullable().optional(), label: z.string().max(100).nullable().optional() })).optional(),
});
export const AreaCreateSchema = AreaBaseSchema.extend({ status: z.enum(['pool', 'lead']).default('pool') });
export type AreaCreate = z.infer<typeof AreaCreateSchema>;
export const AreaPatchSchema = AreaBaseSchema.partial().extend({ updated_at: z.string().optional() });
export type AreaPatch = z.infer<typeof AreaPatchSchema>;
export const AreaStatusSchema = z.object({ status: z.enum(AREA_STATUSES), reason: z.string().max(2000).nullable().optional() });

export interface AreaListItem {
  id: number;
  name: string;
  status: AreaStatus;
  state_code: string | null;
  state_label: string | null;
  region: string | null;
  city: string | null;
  municipality: string | null;
  lat: number | null;
  lng: number | null;
  hectares: number | null;
  forester: { id: number; display_name: string; rating_grade: RatingGrade | null } | null;
  owner: { id: number; display_name: string } | null;
  owner_name_text: string | null;
  planting_season_code: 'F' | 'S' | 'H' | null;
  planting_year: number | null;
  trees_planted: number | null;
  secured_at: string | null;
  monitoring: MonitoringState;
  review_needed: boolean;
  archived_at: string | null;
  updated_at: string;
}
export interface AreaPolygonDto { id?: number; seq: number; lat: number; lng: number; hectares: number | null; label: string | null }
export interface SiteVisitSummary {
  id: number;
  status: 'draft' | 'final';
  visited_at: string | null;
  visited_by: string | null;
  forester_name: string | null;
  trees_planned: number | null;
  price_per_tree_max: number | null;
  updated_at: string;
}
export interface AreaHistoryEntry { at: string; user: string | null; from: string | null; to: string; reason: string | null }
export interface AreaDetail extends AreaListItem {
  rejection_reason: string | null;
  zip: string | null;
  district: string | null;
  flur: string | null;
  parcel: string | null;
  compartment: string | null;
  ownership_type: string | null;
  certification: string | null;
  water_protection: string | null;
  soil: string | null;
  wildlife_protection: string | null;
  ecological_need: string | null;
  neighbour_potential: string | null;
  rescue_point: string | null;
  access_note: string | null;
  parking_lat: number | null;
  parking_lng: number | null;
  planted_at: string | null;
  notes: string | null;
  polygons: AreaPolygonDto[];
  site_visits: SiteVisitSummary[];
  actions: { id: number; code: string; label: string; date: string | null; trees: number | null; status: string; status_label: string; role: string }[];
  history: AreaHistoryEntry[];
  forester_contract_year: number | null;
  created_at: string;
}

/* ---------- Monitoring-Zustand (bis Plan 05 aus Pflanzjahr und Intervallen abgeleitet) ---------- */
export interface MonitoringState {
  kind: 'none' | 'due' | 'overdue' | 'secured';
  next: string | null; // JJJJ-MM
  label: string;
  /** nächster offener Monitoring-Vorgang (Plan 05), null wenn nur aus dem Pflanzjahr abgeleitet */
  id?: number | null;
}
export function monitoringState(
  a: { planting_year: number | null; planting_season_code?: string | null; secured_at: string | null; status: AreaStatus; last_monitoring_year?: number | null },
  today: string,
  intervals: number[] = [2, 5],
  windowToMonth = 9,
): MonitoringState {
  if (a.secured_at || a.status === 'gesichert') return { kind: 'secured', next: null, label: 'gesichert' };
  if (!a.planting_year || ['pool', 'lead', 'in_akquise', 'begangen', 'geplant', 'abgelehnt', 'inaktiv'].includes(a.status)) return { kind: 'none', next: null, label: 'noch keine Pflanzung' };
  const last = a.last_monitoring_year ?? null;
  const years = intervals.map((i) => a.planting_year! + i).filter((y) => last == null || y > last);
  const nextYear = years[0] ?? (last != null ? last + intervals[intervals.length - 1]! : a.planting_year + intervals[0]!);
  const next = `${nextYear}-06`;
  const todayYear = Number(today.slice(0, 4));
  const todayMonth = Number(today.slice(5, 7));
  const overdue = todayYear > nextYear || (todayYear === nextYear && todayMonth > windowToMonth);
  return overdue ? { kind: 'overdue', next, label: 'überfällig' } : { kind: 'due', next, label: `nächstes ${next.slice(5)}/${next.slice(0, 4)}` };
}

/* ---------- Statusübergänge ---------- */
const MANUAL: Record<AreaStatus, AreaStatus[]> = {
  pool: ['lead', 'in_akquise', 'abgelehnt', 'inaktiv'],
  lead: ['in_akquise', 'pool', 'abgelehnt', 'inaktiv'],
  in_akquise: ['begangen', 'abgelehnt', 'inaktiv', 'pool'],
  begangen: ['geplant', 'abgelehnt', 'inaktiv', 'pool'],
  geplant: ['bepflanzt', 'begangen', 'abgelehnt', 'inaktiv'],
  bepflanzt: ['im_monitoring', 'gesichert', 'inaktiv'],
  im_monitoring: ['gesichert', 'inaktiv'],
  gesichert: ['im_monitoring', 'inaktiv'],
  abgelehnt: ['pool'],
  inaktiv: ['pool'],
};
export function allowedTransitions(from: AreaStatus): AreaStatus[] {
  return MANUAL[from] ?? [];
}
export function canTransition(from: AreaStatus, to: AreaStatus): boolean {
  return from !== to && allowedTransitions(from).includes(to);
}
export const needsReason = (to: AreaStatus) => to === 'abgelehnt' || to === 'inaktiv';

/* ---------- Begehungsprotokoll ---------- */
const bool = z.boolean().nullable().optional();
const intn = z.number().int().nullable().optional();

/** Flächenfelder, die im Protokoll angezeigt und in areas gespeichert werden */
export const SiteVisitAreaFieldsSchema = z.object({
  city: str(120),
  municipality: str(120),
  district: str(120),
  flur: str(60),
  parcel: str(60),
  compartment: str(60),
  lat: dec,
  lng: dec,
  hectares: dec,
  ecological_need: z.string().nullable().optional(),
  neighbour_potential: z.string().nullable().optional(),
  owner_contact_id: z.number().int().nullable().optional(),
});
export const SiteVisitFieldsSchema = z.object({
  action_id: z.number().int().nullable().optional(),
  visited_at: str(10),
  visited_by_user_id: z.number().int().nullable().optional(),
  visited_by_text: str(60),
  forester_contact_id: z.number().int().nullable().optional(),
  owner_name_text: str(200),
  trees_planned: intn,
  species_text: str(500),
  species: z.array(z.object({ species_code: z.string().max(50), count: z.number().int().min(0) })).optional(),
  trees_by_participants: intn,
  planting_from: str(60),
  completion_until: str(60),
  helpers_by_forester: bool,
  helpers_count: intn,
  fire_by_forester: bool,
  tree_order_by: z.enum(['forstamt', 'tww']).nullable().optional(),
  planting_plan_by_forester: bool,
  map_by_forester: bool,
  remaining_planting_by: str(50),
  clearing: str(50),
  preparation: str(50),
  planting_holes: str(50),
  wildlife_protection: str(50),
  wildlife_note: z.string().nullable().optional(),
  soil: z.string().nullable().optional(),
  price_per_tree_max: dec,
  cost_note: z.string().nullable().optional(),
  rescue_point: str(60),
  meeting_point_ok: bool,
  performance_space: bool,
  bus_access: z.string().nullable().optional(),
  car_access: z.string().nullable().optional(),
  parking_count: intn,
  parking_lat: dec,
  parking_lng: dec,
  street_name: str(200),
  signage: z.string().nullable().optional(),
  access_work: z.string().nullable().optional(),
  press_contact: z.string().nullable().optional(),
  caterer_suggestion: z.string().nullable().optional(),
  caterer_wc_space: bool,
  agreements: z.string().nullable().optional(),
  drought_measures: z.string().nullable().optional(),
  certification: str(50),
  other_notes: z.string().nullable().optional(),
  signed_forester_at: str(10),
  signed_tww_at: str(10),
  client_uuid: z.string().uuid().nullable().optional(),
});
export const SiteVisitPatchSchema = SiteVisitFieldsSchema.partial().extend({ area: SiteVisitAreaFieldsSchema.partial().optional() });
export type SiteVisitPatch = z.infer<typeof SiteVisitPatchSchema>;
export type SiteVisitFields = z.infer<typeof SiteVisitFieldsSchema>;
export type SiteVisitAreaFields = z.infer<typeof SiteVisitAreaFieldsSchema>;

export interface SiteVisitDto extends Omit<SiteVisitFields, 'client_uuid'> {
  id: number;
  area_id: number;
  status: 'draft' | 'final';
  finalized_at: string | null;
  area: SiteVisitAreaFields & { id: number; name: string; status: AreaStatus; state_code: string | null; region: string | null; owner_name_text: string | null };
  forester: { id: number; display_name: string; phone: string | null; mobile: string | null; email: string | null; rating_grade: RatingGrade | null; last_price: { price: number; year: number } | null } | null;
  owner: { id: number; display_name: string } | null;
  visited_by: string | null;
  action: { id: number; code: string; label: string } | null;
  checklist_stub: Record<string, unknown> | null;
  updated_at: string;
  created_at: string;
}

export const SyncItemSchema = z.object({
  client_uuid: z.string().uuid(),
  area_id: z.number().int().nullable().optional(),
  updated_at: z.string(),
  fields: SiteVisitPatchSchema,
  new_area: AreaCreateSchema.partial().optional(),
});

/* ---------- Regeln (Plan 03 Abschnitt 6.3 und 6.4) ---------- */
export interface SiteVisitValidation { errors: Record<string, string>; warnings: string[] }
export function validateSiteVisitForFinalize(v: Partial<SiteVisitFields> & { area?: Partial<SiteVisitAreaFields> }): SiteVisitValidation {
  const errors: Record<string, string> = {};
  const warnings: string[] = [];
  if (!v.visited_at) errors.visited_at = 'Begehungsdatum fehlt';
  if (!v.forester_contact_id) errors.forester_contact_id = 'Förster fehlt';
  if (!v.area?.city?.trim()) errors['area.city'] = 'Ort fehlt';
  if (!v.area?.hectares && !v.trees_planned) errors.trees_planned = 'Hektar oder Anzahl Bäume fehlt';
  if (!v.wildlife_protection) errors.wildlife_protection = 'Wildschutz angeben (auch „kein Wildschutz“)';
  if (v.price_per_tree_max == null && !v.cost_note?.trim()) errors.price_per_tree_max = 'Preis pro Baum oder Kostenanmerkung fehlt';
  const coordWarn = (lat: number | null | undefined, lng: number | null | undefined, what: string) => {
    if (lat == null && lng == null) return;
    if (lat == null || lng == null || lat < 47 || lat > 55 || lng < 5 || lng > 16) warnings.push(`${what}: Koordinaten bitte prüfen (Dezimalgrad, Deutschland)`);
  };
  coordWarn(v.area?.lat, v.area?.lng, 'Fläche');
  coordWarn(v.parking_lat, v.parking_lng, 'Parken');
  if (v.species?.length && v.trees_planned != null) {
    const sum = v.species.reduce((s, x) => s + (x.count || 0), 0);
    if (sum !== v.trees_planned) warnings.push(`Summe der Baumarten (${sum}) weicht von der Anzahl Bäume (${v.trees_planned}) ab`);
  }
  return { errors, warnings };
}

/** Koordinaten "53.244, 9.962" oder "53,244; 9,962" lesen */
export function parseCoords(s: string | null | undefined): { lat: number; lng: number } | null {
  if (!s) return null;
  const m = /(-?\d+[.,]\d+)[^\d-]+(-?\d+[.,]\d+)/.exec(s);
  if (m) return { lat: Number(m[1]!.replace(',', '.')), lng: Number(m[2]!.replace(',', '.')) };
  const nums = s.split(/[\s,;]+/).map(Number).filter((n) => !Number.isNaN(n));
  return nums.length >= 2 ? { lat: nums[0]!, lng: nums[1]! } : null;
}
export const fmtCoords = (lat: number | null | undefined, lng: number | null | undefined) => (lat == null || lng == null ? '' : `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`);
export const mapsUrl = (lat: number, lng: number) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

/** Vorschlag für den Flächennamen: "Ort, Abt. 9a" */
export function suggestAreaName(a: { city?: string | null; municipality?: string | null; compartment?: string | null; district?: string | null }): string {
  const place = a.city?.trim() || a.municipality?.trim() || a.district?.trim() || 'Fläche';
  return a.compartment?.trim() ? `${place}, Abt. ${a.compartment.trim()}` : place;
}

/**
 * Übernahme in die Checkliste (Abschnitt 6.3 Schritt 4) als reine Zuordnung. Plan 04 füllt damit nur leere Felder.
 * Bis dahin wird das Ergebnis am Protokoll gespeichert (checklist_stub) und protokolliert.
 */
export function checklistFromSiteVisit(v: Partial<SiteVisitFields> & { area?: Partial<SiteVisitAreaFields> }, ctx: { foresterName?: string | null; ownerName?: string | null; visitedBy?: string | null }): Record<string, Record<string, unknown>> {
  const isNo = (m: string | null | undefined) => !m || m === 'nein';
  const out: Record<string, Record<string, unknown>> = {
    foerster: { kontakt: ctx.foresterName ?? '' },
    waldbesitzer: { kontakt: ctx.ownerName ?? v.owner_name_text ?? '' },
    besichtigung: { status: 'Erledigt', geplant: v.visited_at, erledigt: v.visited_at, ort: v.area?.city, schaden: v.area?.ecological_need, dateien: ['Begehungsprotokoll.pdf'] },
    zust_besichtigung: { name: ctx.visitedBy ?? v.visited_by_text ?? '', erledigt: v.visited_at },
    baeume: { durch: v.tree_order_by === 'tww' ? 'TWW' : v.tree_order_by === 'forstamt' ? 'Förster' : '' },
    raeumung: { noetig: v.clearing ? (isNo(v.clearing) ? 'Nein' : 'Ja') : '' },
    flvorbereitung: { noetig: v.preparation ? (isNo(v.preparation) ? 'Nein' : 'Ja') : '' },
    pflanzloch: { noetig: v.planting_holes ? (isNo(v.planting_holes) ? 'Nein' : 'Ja') : '' },
    wildschutz: { noetig: v.wildlife_protection ? (v.wildlife_protection === 'kein' ? 'Nein' : 'Ja') : '', art: v.wildlife_protection && v.wildlife_protection !== 'kein' ? v.wildlife_protection : '' },
    lotsen_foerster: { ja: v.helpers_by_forester == null ? '' : v.helpers_by_forester ? 'Ja' : 'Nein', anzahl: v.helpers_count ?? '' },
    caterer: { benoetigt: 'benötigt', kontakt: v.caterer_suggestion ?? '' },
    presse: { benoetigt: v.press_contact ? 'benötigt' : '', forst: v.press_contact ? 'Ja' : '' },
    bus: { benoetigt: v.bus_access ? 'benötigt' : '' },
    toiletten: { benoetigt: 'benötigt' },
    lotsen: { benoetigt: 'benötigt' },
    eww: { benoetigt: 'benötigt' },
    reise: { benoetigt: 'benötigt' },
    vertrag: { ja: 'Nein' },
  };
  return out;
}

/** Nur leere Zielwerte füllen (Prototyp applyProtokoll) */
export function fillEmpty<T extends Record<string, unknown>>(target: T, source: Record<string, unknown>): T {
  const out = { ...target };
  for (const [k, v] of Object.entries(source)) {
    const cur = (out as Record<string, unknown>)[k];
    if ((cur === undefined || cur === null || cur === '') && v !== undefined && v !== null && v !== '') (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

/** Abschnitte des Protokollformulars mit den Feldern, für Zähler "x von y ausgefüllt" und Druckansicht */
export const SITE_VISIT_SECTIONS: { key: string; title: string; fields: string[] }[] = [
  { key: 'kopf', title: 'Begehung', fields: ['visited_at', 'visited_by_user_id', 'forester_contact_id'] },
  { key: 'flaeche', title: 'Fläche', fields: ['area.city', 'area.municipality', 'area.district', 'area.flur', 'area.parcel', 'area.compartment', 'area.lat', 'area.hectares', 'owner_name_text', 'area.ecological_need', 'area.neighbour_potential'] },
  { key: 'pflanzung', title: 'Pflanzung', fields: ['trees_planned', 'species_text', 'planting_from', 'completion_until', 'trees_by_participants'] },
  { key: 'helfer', title: 'Pflanzhelfer und Feuer', fields: ['helpers_by_forester', 'helpers_count', 'fire_by_forester'] },
  { key: 'plaene', title: 'Bäume und Pläne', fields: ['tree_order_by', 'planting_plan_by_forester', 'map_by_forester', 'remaining_planting_by'] },
  { key: 'vorbereitung', title: 'Vorbereitung und Wildschutz', fields: ['clearing', 'preparation', 'planting_holes', 'wildlife_protection', 'wildlife_note', 'soil'] },
  { key: 'kosten', title: 'Kostenvereinbarung', fields: ['price_per_tree_max', 'cost_note'] },
  { key: 'logistik', title: 'Logistik', fields: ['rescue_point', 'meeting_point_ok', 'bus_access', 'car_access', 'parking_count', 'parking_lat', 'street_name', 'signage', 'access_work'] },
  { key: 'event', title: 'Event und Absprachen', fields: ['press_contact', 'caterer_suggestion', 'caterer_wc_space', 'agreements', 'drought_measures', 'certification', 'other_notes'] },
  { key: 'abschluss', title: 'Abschluss', fields: ['signed_forester_at', 'signed_tww_at'] },
];
export function sectionProgress(v: Record<string, unknown> & { area?: Record<string, unknown> }, fields: string[]): { filled: number; total: number } {
  let filled = 0;
  for (const f of fields) {
    const val = f.startsWith('area.') ? v.area?.[f.slice(5)] : v[f];
    if (val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && !val.length)) filled++;
  }
  return { filled, total: fields.length };
}
